/**
 * useMeetingHeaderActions — per-entity-type header actions for meetings.
 *
 * Returns `ResponsiveAction[]` for the record header's action bar. For non-meeting
 * entities, every inner hook receives `undefined` args and short-circuits (no queries,
 * no timers) — the returned array is always the same empty reference.
 *
 * Contributes two actions, with deliberately different placements:
 *
 *  - **"Show on dashboard"** (`placement: 'inline'`) — restores a meeting the current
 *    user has dismissed, rendering as a button beside the built-in CTAs like "Add note".
 *    Shown only when the meeting (or its series) is in the current user's hidden-meeting
 *    set (useHiddenMeetings), and unlike the recording toggle it is NOT hidden for past
 *    meetings: a dismissal must stay reversible whenever the user can reach the record.
 *
 *  - **"Record this meeting" / "Don't record this meeting"** (`placement: 'dropdown'`)
 *    — a bidirectional toggle overriding the user's default notetaker setting for a
 *    specific meeting. Hidden for past meetings and when Recall isn't enabled.
 *    Effective state comes from the per-meeting override
 *    (`ahoy_notetaker_force_record`) then the user's default (`method_preference`);
 *    the toggle always sets the opposite.
 *
 *    On a platform the bot cannot join, the action is shown **disabled with an
 *    explanatory tooltip** rather than hidden (ResponsiveActionBar surfaces
 *    `tooltip.title` on a disabled dropdown item via `Dropdown.Item`'s
 *    `disabledTooltip`). The backend scheduler drops such meetings before they
 *    ever become bot candidates, so an enabled toggle here would take the click,
 *    show "Recording turned on for this meeting", and deliver nothing — a false
 *    promise is worse than an unavailable control.
 */

import { Eye, VideoRecorder } from '@untitledui/icons';
import { useCallback, useMemo } from 'react';
import { toast } from 'sonner';

import { useForceRecord } from '@/hooks/integrations/recall/useForceRecord';
import { useRecallPreference } from '@/hooks/integrations/recall/useRecallPreference';
import type { ResponsiveAction } from '@/lib/base/buttons';
import {
  isNotetakerSupportedMeetingUrl,
  NOTETAKER_SUPPORTED_PLATFORMS_SENTENCE,
} from '@/lib/crm/utils/notetakerPlatforms';
import { useHiddenMeetings } from '@/lib/dashboard/hiddenMeetings';
import type { CrmObject } from '@/types/crm';
import { getPropertyValue } from '@/types/crm';

import { useIsMeetingPast } from './useIsMeetingPast';
import { useUndismissMeeting } from './useUndismissMeeting';

const EMPTY_ACTIONS: ResponsiveAction[] = [];

const UNSUPPORTED_PLATFORM_TOOLTIP = {
  title: 'Ahoy cannot join this meeting',
  description: `The notetaker joins ${NOTETAKER_SUPPORTED_PLATFORMS_SENTENCE} calls. This meeting has no join link Ahoy recognises, so no bot will attend. The Ahoy desktop app can still record it locally.`,
};

const UNDISMISS_SINGLE_DESCRIPTION =
  "We'll show this meeting on your dashboard again. Only you see this change.";
const UNDISMISS_SERIES_DESCRIPTION =
  "We'll show this meeting and the rest of its series on your dashboard again. Only you see this change.";

export function useMeetingHeaderActions(
  object: CrmObject | null
): ResponsiveAction[] {
  const isMeeting = object?.entity_type_name === 'ahoy_meeting';

  // Pass undefined for non-meetings (or null object) so hooks skip their
  // queries/timers — React Query disables queries when `enabled` is false,
  // and mutation hooks only fire on explicit calls.
  const orgId = isMeeting ? object.org_id : undefined;
  const meetingEntityId = isMeeting ? object.entity_id : undefined;
  const startTime = isMeeting
    ? getPropertyValue<string>(object.properties?.ahoy_start_time)
    : null;

  const isPast = useIsMeetingPast(startTime);
  const { available, loading, preference } = useRecallPreference(orgId);
  const { setForceRecord, updating, reset } = useForceRecord(
    orgId,
    meetingEntityId
  );

  // User's default recording state: any method_preference other
  // than "disabled" means the user has opted in to recording.
  const userEnabled =
    preference != null && preference.method_preference !== 'disabled';

  // Per-meeting override: "true" = force on, "false" = force off,
  // absent = follow user default.
  const overrideValue =
    object != null
      ? getPropertyValue<string>(object.properties?.ahoy_notetaker_force_record)
      : null;

  // Effective recording state: override wins, then user default.
  const isRecordingOn =
    overrideValue === 'true' || (overrideValue !== 'false' && userEnabled);

  // Can a bot actually reach this call? Mirrors the backend scheduler's
  // platform filter — see notetakerPlatforms.ts.
  const supportsBot = isNotetakerSupportedMeetingUrl(
    isMeeting
      ? getPropertyValue<string>(object.properties?.ahoy_meeting_link)
      : null
  );

  // On an unsupported platform the only honest label is the aspirational one:
  // "Don't record this meeting" would imply a recording is otherwise coming.
  const recordingToggleLabel =
    supportsBot && isRecordingOn
      ? "Don't record this meeting"
      : 'Record this meeting';

  const handleToggle = useCallback(() => {
    reset();
    // Toggle to the opposite of the current effective state.
    const newValue = !isRecordingOn;
    void setForceRecord(newValue)
      .then(() =>
        toast.success(
          newValue
            ? 'Recording turned on for this meeting'
            : 'Recording turned off for this meeting'
        )
      )
      .catch(() => toast.error("We couldn't update the recording. Try again."));
  }, [isRecordingOn, reset, setForceRecord]);

  // Key the copy off the series id rather than `ahoy_is_recurring`: the series id is
  // exactly what the backend fan-out keys on, so this is what decides whether siblings
  // are actually affected. Using the other flag risks promising a series-wide change
  // that doesn't happen (or staying silent about one that does).
  const seriesId = isMeeting
    ? getPropertyValue<string>(object.properties?.ahoy_recurring_series_id)
    : null;
  const affectsSeries = seriesId != null && String(seriesId).trim() !== '';

  // Undismiss is offered only when the CURRENT user has dismissed this meeting.
  // Keying it off "anyone dismissed it" would offer to undo a colleague's
  // dismissal, and showing it unconditionally would put a meaningless button on
  // every meeting.
  const { undismissMeeting, isUndismissing } = useUndismissMeeting(orgId ?? 0);
  const { hiddenMeetings } = useHiddenMeetings(orgId);
  const isDismissedByMe =
    (meetingEntityId != null &&
      hiddenMeetings.meetingEntityIds.includes(meetingEntityId)) ||
    (seriesId != null &&
      seriesId !== '' &&
      hiddenMeetings.seriesIds.includes(seriesId));

  const handleUndismiss = useCallback(() => {
    if (meetingEntityId == null) {
      return;
    }
    void undismissMeeting(meetingEntityId).catch(() =>
      toast.error("We couldn't show this meeting on your dashboard. Try again.")
    );
  }, [undismissMeeting, meetingEntityId]);

  return useMemo(() => {
    const actions: ResponsiveAction[] = [];
    if (!isMeeting) {
      return EMPTY_ACTIONS;
    }
    // Deliberately NOT gated on isPast/available like the recording toggle: a
    // dismissed meeting must always be recoverable, including a past one.
    if (isDismissedByMe && meetingEntityId != null) {
      actions.push({
        key: 'meeting-undismiss',
        label: 'Show on dashboard',
        icon: Eye,
        onClick: handleUndismiss,
        isDisabled: isUndismissing(meetingEntityId),
        placement: 'inline' as const,
        tooltip: {
          title: 'Show on dashboard',
          description: affectsSeries
            ? UNDISMISS_SERIES_DESCRIPTION
            : UNDISMISS_SINGLE_DESCRIPTION,
        },
      });
    }
    if (!isPast && !loading && available) {
      actions.push({
        key: 'meeting-recording-toggle',
        label: recordingToggleLabel,
        icon: VideoRecorder,
        onClick: handleToggle,
        isDisabled: updating || !supportsBot,
        placement: 'dropdown' as const,
        // Unsupported platform: ResponsiveActionBar passes tooltip.title as
        // the disabled dropdown item's `disabledTooltip`, so the user sees why
        // the toggle can't be used.
        ...(supportsBot ? {} : { tooltip: UNSUPPORTED_PLATFORM_TOOLTIP }),
      });
    }
    return actions.length === 0 ? EMPTY_ACTIONS : actions;
  }, [
    isMeeting,
    isPast,
    loading,
    available,
    recordingToggleLabel,
    supportsBot,
    handleToggle,
    updating,
    isDismissedByMe,
    meetingEntityId,
    handleUndismiss,
    isUndismissing,
    affectsSeries,
  ]);
}
